/**
 * Email-verification banner — pure view-model (PR-ONB-EMAIL-VERIFY-HONESTY).
 *
 * Centralises the HONESTY rule: the UI claims the verification link was sent
 * ONLY when Firebase actually accepted the send (`sendEmailVerification`
 * resolved). When the send was rejected (e.g. the `sendOobCode` 400 observed on
 * dev) or when there is no proof of a successful send (the area status page on
 * load), the copy must stay neutral and never assert "ti abbiamo inviato".
 *
 * No `import.meta`, no React — so it is unit-testable in isolation without a
 * heavy component render (see `__tests__/emailVerificationView.test.ts`).
 */

export type EmailVerificationSendOutcome = 'sent' | 'failed' | 'unknown';

export type EmailVerificationBannerTone = 'info' | 'success' | 'warning';

export interface EmailVerificationBannerView {
  /** Whether the banner should render at all (false once the email is verified). */
  show: boolean;
  tone: EmailVerificationBannerTone;
  title: string;
  message: string;
  /** True ONLY when the copy asserts the link was actually sent. */
  claimsSent: boolean;
}

/**
 * Maps `(emailVerified, sendOutcome)` to the banner to render.
 *
 * - `sent`    → positive "link inviato" copy (the only branch that may claim it).
 * - `failed`  → honest warning, no false confirmation.
 * - `unknown` → neutral copy (no proof a send succeeded), resend CTA expected
 *   from the host component.
 */
export function buildEmailVerificationBanner(input: {
  emailVerified: boolean;
  sendOutcome: EmailVerificationSendOutcome;
}): EmailVerificationBannerView {
  if (input.emailVerified) {
    return { show: false, tone: 'success', title: '', message: '', claimsSent: false };
  }

  switch (input.sendOutcome) {
    case 'sent':
      return {
        show: true,
        tone: 'info',
        title: 'Verifica la tua email',
        message:
          'Ti abbiamo inviato un link di verifica. Conferma la tua email quando puoi: ' +
          'puoi continuare la compilazione, ma dovrai completare la verifica prima di ' +
          'inviare la richiesta.',
        claimsSent: true,
      };
    case 'failed':
      return {
        show: true,
        tone: 'warning',
        title: 'Verifica email non inviata',
        message:
          'Non siamo riusciti a inviare ora il link di verifica. Puoi continuare la ' +
          'compilazione: potrai richiedere l’invio del link dalla tua area, sezione ' +
          '“Stato richiesta”. La verifica sarà necessaria solo per inviare la ' +
          'richiesta al controllo preliminare.',
        claimsSent: false,
      };
    case 'unknown':
    default:
      return {
        show: true,
        tone: 'info',
        title: 'Conferma la tua email',
        message:
          'Conferma la tua email quando puoi. Puoi continuare la compilazione, ma ' +
          'dovrai completare la verifica prima di inviare la richiesta. Se non hai ' +
          'ricevuto il link, premi “Invia nuovamente l’email”.',
        claimsSent: false,
      };
  }
}

/**
 * Derives the send outcome from the optimistic-send tri-state used by the
 * account-creation screen: `true` → accepted, `false` → rejected, `null`
 * (not attempted / unknown, e.g. reaching the screen via resume) → neutral.
 */
export function sendOutcomeFromAttempt(
  verificationSent: boolean | null,
): EmailVerificationSendOutcome {
  if (verificationSent === true) return 'sent';
  if (verificationSent === false) return 'failed';
  return 'unknown';
}
